import {
  KeyFacet,
  stringPart,
  subKeyPart,
  booleanPart,
  numberPart,
  arrayPart
} from '@ms/items-view/lib/private/models/KeyFacet';

/**
 * ID for the needs attention view
 * @internal
 */
export const needsAttentionViewId = 'f9ebc8b2-a976-48f0-a8fa-0846164c0466';

/**
 * Key facet for an item which represents a SharePoint web.
 * @public
 */
export const spWebKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
}> = new KeyFacet('spWeb', {
  webAbsoluteUrl: stringPart()
});

/**
 * Key facet for an item within a SharePoint list.
 * Items using this key facet contain file and folder metadata, such as names, extensions, item types,
 * thumbnail data, sharing state, etc.
 * @public
 */
export const spItemKeyFacet: KeyFacet<{
  /**
   * The fully-qualified URL to the root of the web. No trailing '/'.
   */
  webAbsoluteUrl: string;
  /**
   * The fully-qualified URL to the root of the list. No trailing '/'.
   */
  listFullUrl: string;
  /**
   * The id assigned to this item by its parent list. Durable.
   */
  itemId?: string; // TODO rename this to something more clear. Also, use a dedicated KeyFacet for lists themselves.
  /**
   * A server-relative path (beginning with '/' after the domain) to the item, if it is a folder. Do not include a trailing '/'.
   */
  rootFolder?: string;
}> = new KeyFacet('spItem', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  itemId: stringPart({ shouldNormalizeToEmpty: true }),
  rootFolder: stringPart({ shouldNormalizeToEmpty: true }),
});

/**
 * @beta
 */
export const spItemFullUrlKeyFacet: KeyFacet<{
  itemFullUrl: string;
}> = new KeyFacet('spItemFullUrl', {
  itemFullUrl: stringPart()
});

/**
 * Key facet for an item which represents a SharePoint List itself.
 * Items using this key facet contain schema and view information about the list.
 *
 * This is distinct from the root item in a list, for which spItemKeyFacet should be used.
 *
 * @internal
 */
export const spListKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('spList', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for an item which represents a SharePoint ListView collection.
 * @internal
 */
export const spListViewsSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('spListViews', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for an item which represents a SharePoint field belonging to a List.
 * @internal
 */
export const spFieldKeyFacet: KeyFacet<{
  // TODO This needs the spListKey, or else two lists with the same column name will collide on the definition.
  internalName: string;
}> = new KeyFacet('spField', {
  internalName: stringPart()
});

/**
 * Key facet for an item which represents a the client-side column adapter on a SharePoint field
 * @internal
 */
export const spColumnAdapterKeyFacet: KeyFacet<{
  // TODO This needs the spListKey, or else two lists with the same column name will collide on the definition.
  internalName: string;
}> = new KeyFacet('spColumnAdapter', {
  internalName: stringPart()
});

/**
 * Key facet for an item which represents a SharePoint view.
 * Items using this key facet have metadata, such as the CAML, for a single view within a list.
 * @public
 */
export const spViewKeyFacet: KeyFacet<{
  viewId: string;
}> = new KeyFacet('spView', {
  viewId: stringPart()
});

/**
 * Key facet for an item which represents a list of people who liked the SharePoint page.
 * Items using this key facet have liked people metadata, such as people display name, email, etc, for a single person within a list.
 * @public
 */
export const likedByItemKeyFacet: KeyFacet<{
  spItem: typeof spItemKeyFacet.type;
}> = new KeyFacet('likedByItem', {
  spItem: subKeyPart(spItemKeyFacet)
});

/**
 * Key facet for an item which represents Page Context for a SharePoint List.
 *
 * TODO it would be great to split the information up based on 'web' and 'list' and just store
 * the data using 'spListKeyFacet' and 'spWebKeyFacet'.
 * @internal
 */
export const spPageContextKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('spPageContext', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for an item which represents Page Context for a SharePoint Web.
 * @internal
 */
export const spWebPageContextKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
}> = new KeyFacet('spWebPageContext', {
  webAbsoluteUrl: stringPart()
});

/**
 * Key facet for _spPageContextInfo that is only used to initialize DataRequestor.
 * It contains properties defined in IDataRequestorPageContext, which does not have
 * List level properties.
 * @internal
 */
export const spPageContextForDataRequestorKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
}> = new KeyFacet('spPageContextForDataRequestor', {
  webAbsoluteUrl: stringPart()
});

/**
 * Key facet for an item set of a folder's immediate child items.
 * @public
 */
export const spFolderItemSetKeyFacet: KeyFacet<{
  /**
   * The fully-qualified URL to the root of the web. No trailing '/'.
   */
  webAbsoluteUrl: string;
  /**
   * The fully-qualified URL to the root of the list. No trailing '/'.
   */
  listFullUrl: string;
  /**
   * A server-relative path (beginning with '/' after the domain) to the folder. Do not include a trailing '/'.
   */
  rootFolder: string;
  sortFieldKey?: string;
  sortAscending?: boolean;
  filterParams?: string;
  groupByFieldKey?: string;
  disableGrouping?: boolean;
  viewKey?: typeof spViewKeyFacet.type;
  viewPath?: string;
  extFilters?: string[];
}> = new KeyFacet('spFolderItemSet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  rootFolder: stringPart(),
  sortFieldKey: stringPart(),
  sortAscending: booleanPart(),
  filterParams: stringPart(),
  groupByFieldKey: stringPart(),
  disableGrouping: booleanPart(),
  viewKey: subKeyPart(spViewKeyFacet),
  viewPath: stringPart(),
  extFilters: arrayPart(stringPart())
});

/**
 * Key facet for an item set of a folder with a search term
 */
export const spSearchItemSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
  rootFolder: string;
  sortFieldKey?: string;
  sortAscending?: boolean;
  filterParams?: string;
  groupByFieldKey?: string;
  disableGrouping?: boolean;
  viewKey?: typeof spViewKeyFacet.type;
  viewPath?: string;
  extFilters?: string[];
  searchTerm?: string;
}> = new KeyFacet('spSearchItemSet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  rootFolder: stringPart(),
  sortFieldKey: stringPart(),
  sortAscending: booleanPart(),
  filterParams: stringPart(),
  groupByFieldKey: stringPart(),
  disableGrouping: booleanPart(),
  viewKey: subKeyPart(spViewKeyFacet),
  viewPath: stringPart(),
  extFilters: arrayPart(stringPart()),
  searchTerm: stringPart()
});

/**
 * @internal
 */
export const spFolderMediaGroupingItemSetKeyFacet: KeyFacet<{
  spFolderItemSet: typeof spFolderItemSetKeyFacet.type;
}> = new KeyFacet('spFolderMediaGroupingItemSet', {
  spFolderItemSet: subKeyPart(spFolderItemSetKeyFacet)
});

/**
 * @internal
 */
export const spFolderMediaGroupingItemSetGroupKeyFacet: KeyFacet<{
  spFolderItemSet: typeof spFolderItemSetKeyFacet.type;
  group: string;
}> = new KeyFacet('spFolderMediaGroupingItemSetGroup', {
  spFolderItemSet: subKeyPart(spFolderItemSetKeyFacet),
  group: stringPart()
});

/**
 * @internal
 */
export const spFolderMediaGroupingItemSetPageKeyFacet: KeyFacet<{
  spFolderItemSet: typeof spFolderItemSetKeyFacet.type;
  pageKey: string;
}> = new KeyFacet('spFolderMediaGroupingItemSetPage', {
  spFolderItemSet: subKeyPart(spFolderItemSetKeyFacet),
  pageKey: stringPart()
});

/**
 * @public
 * @deprecated Use spFolderItemSetKeyFacet instead.
 * Using this key will redirect to the new key facet.
 */
export const spItemSetKeyFacet: KeyFacet<{
  parentItemKey: typeof spItemKeyFacet.type;
  sortFieldKey?: string;
  sortAscending?: boolean;
  filterParams?: string;
  groupByFieldKey?: string;
  viewKey?: typeof spViewKeyFacet.type;
}> = new KeyFacet('spItemSet', {
  parentItemKey: subKeyPart(spItemKeyFacet),
  sortFieldKey: stringPart(),
  sortAscending: booleanPart(),
  filterParams: stringPart(),
  groupByFieldKey: stringPart(),
  viewKey: subKeyPart(spViewKeyFacet)
});

/**
 * Intermediate key facet which can be used to determine if an item set
 * contains items that need attention.
 * @internal
 */
export const spNeedsAttentionItemKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('spNeedsAttentionItem', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for an item which represents a group within a parent item set.
 * Items using this metadata are not 'real' items, but are nested item sets.
 * @internal
 */
export const spItemGroupKeyFacet: KeyFacet<{
  groupId: string;
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  viewKey?: typeof spViewKeyFacet.type;
}> = new KeyFacet('spItemGroup', {
  groupId: stringPart(),
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  viewKey: subKeyPart(spViewKeyFacet)
});

/**
 * Key facet for an item which represents a person.
 * A person has a profile and persona information.
 * @internal
 */
export const spPersonKeyFacet: KeyFacet<{
  id: string;
}> = new KeyFacet('spPerson', {
  id: stringPart()
});

/**
 * Key facet for an item which represents a 'page' of yet-unresolved items within a parent item set.
 * @internal
 */
export const spItemPageKeyFacet: KeyFacet<{
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  token: string;
}> = new KeyFacet('spItemPage', {
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  token: stringPart()
});

/**
 * Key facet for storing previous page token and next page token for a 'page' of items.
 * This is used for spGrid since it currently doesn't support infinite scrolling.
 * To support paging, we need to store prevHref and nextHref.
 * @internal
 */
export const spFolderItemSetPagingTokenKeyFacet: KeyFacet<{
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  currentFirstRow: number
}> = new KeyFacet('spFolderItemSetPagingTokenKeyFacet', {
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  currentFirstRow: numberPart()
});

/**
 * Key facet for an item which represents a 'page' of unresolved items within a group.
 * These pages are parameterized with paging tokens.
 * @internal
 */
export const spItemTokenGroupPageKeyFacet: KeyFacet<{
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  parentGroupKey: string;
  token: string;
}> = new KeyFacet('spItemTokenGroupPage', {
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  parentGroupKey: stringPart(),
  token: stringPart()
});

/**
 * Key facet for an item which represents a 'page' of unresolved items within a group.
 * These pages are parameters by the previous group and a CAML query for the next group.
 * @internal
 */
export const spItemNextGroupPageKeyFacet: KeyFacet<{
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  nextGroupXml: string;
  nextGroupLevel: number;
  filterParams?: string;
  token?: string;
}> = new KeyFacet('spItemNextGroupPage', {
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  lastGroupKey: stringPart(),
  nextGroupXml: stringPart(),
  nextGroupLevel: numberPart(),
  filterParams: stringPart(),
  token: stringPart()
});

/**
 * Key facet for an item which represents a set of filterable values for a field within a parent item set.
 * @internal
 */
export const spFilterOptionSetKeyFacet: KeyFacet<{
  itemSetKey: typeof spFolderItemSetKeyFacet.type;
  fieldKey: typeof spFieldKeyFacet.type;
}> = new KeyFacet('spFilterOptionSet', {
  itemSetKey: subKeyPart(spFolderItemSetKeyFacet),
  fieldKey: subKeyPart(spFieldKeyFacet)
});

/**
 * Key facet for an item which represents the options for a lookup field within an item set.
 * @internal
 */
export const spLookupItemKeyFacet: KeyFacet<{
  fieldName: string;
  lookupId: string;
}> = new KeyFacet('spLookupItem', {
  fieldName: stringPart(),
  lookupId: stringPart()
});

/**
 * @internal
 */
export const spListContentTypeKeyFacet: KeyFacet<{
  listKey: typeof spListKeyFacet.type;
  contentTypeId: string;
}> = new KeyFacet('spListContentType', {
  listKey: subKeyPart(spListKeyFacet),
  contentTypeId: stringPart()
});

/**
 * This represents a ContentType available within a given folder
 * @internal
 */
export const spFolderContentTypeKeyFacet: KeyFacet<{
  folderKey: typeof spItemKeyFacet.type;
  contentTypeId: string;
}> = new KeyFacet('spFolderContentTypeOrder', {
  folderKey: subKeyPart(spItemKeyFacet),
  contentTypeId: stringPart()
});

export const spNewDocumentTemplateKeyFacet: KeyFacet<{
  listKey: typeof spListKeyFacet.type;
  templateUrl: string;
}> = new KeyFacet('spNewDocumentTemplateKeyFacet', {
  listKey: subKeyPart(spListKeyFacet),
  templateUrl: stringPart()
});

/**
 * @internal
 */
export const spContentTypeCreateDocumentActionKeyFacet: KeyFacet<{
  listKey: typeof spListKeyFacet.type;
  templateUrl: string;
}> = new KeyFacet('spContentTypeCreateDocumentAction', {
  listKey: subKeyPart(spListKeyFacet),
  templateUrl: stringPart()
});

/**
 * @internal
 */
export const spContentTypeNavigationActionKeyFacet: KeyFacet<{
  listKey: typeof spListKeyFacet.type;
  contentTypeId: string;
}> = new KeyFacet('spContentTypeNavigationActionKeyFacet', {
  listKey: subKeyPart(spListKeyFacet),
  contentTypeId: stringPart()
});

/**
 * Key facet for an item which represents a set of items 'pinned' within a list folder.
 * @internal
 */
export const spPinnedItemSetKeyFacet: KeyFacet<{
  parentItemKey: typeof spItemKeyFacet.type;
  viewKey?: typeof spViewKeyFacet.type;
}> = new KeyFacet('spPinnedItemSetKeyFacet', {
  parentItemKey: subKeyPart(spItemKeyFacet),
  viewKey: subKeyPart(spViewKeyFacet)
});

/**
 * Key facet for grid initialization info of a list
 * @internal
 */
export const spGridInitInfoKeyFacet: KeyFacet<{
  listKey: typeof spListKeyFacet.type;
  viewKey?: typeof spViewKeyFacet.type;
}> = new KeyFacet('spGridInitInfoKeyFacet', {
  listKey: subKeyPart(spListKeyFacet),
  viewKey: subKeyPart(spViewKeyFacet)
});

/**
 * Key facet for a custom action
 * todo: ahackett adding the Id (GUID) to SPCustomActionElement
 * so that it can be used as key of custom action.
 * @internal
 */
export const customActionKeyFacet: KeyFacet<{
  actionId: string;
}> = new KeyFacet('customActionKeyFacet', {
  actionId: stringPart()
});

/**
 * Key facet for set of custom actions on the list
 * @internal
 */
export const customActionSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
  spfxLoaded?: boolean;
  itemsScopeItemKey?: string;
}> = new KeyFacet('customActionSetKeyFacet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  spfxLoaded: booleanPart(),
  itemsScopeItemKey: stringPart()
});

/**
 * Key facet for set of legacy custom actions on the list
 * @internal
 */
export const legacyCustomActionSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
  spfxLoaded?: boolean;
  itemsScopeItemKey?: string;
}> = new KeyFacet('legacyCustomActionSetKeyFacet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  spfxLoaded: booleanPart(),
  itemsScopeItemKey: stringPart()
});

/**
 * Key facet for set of tenant application custom actions on the list
 * @internal
 */
export const tenantAppCustomActionSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
  spfxLoaded?: boolean;
  itemsScopeItemKey?: string;
}> = new KeyFacet('tenantAppCustomActionSetKeyFacet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart(),
  spfxLoaded: booleanPart(),
  itemsScopeItemKey: stringPart()
});

/**
 * Key facet for set of list custom actions for context menu
 * @internal
 */
export const contextMenuCustomActionSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('contextMenuCustomActionSetKeyFacet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for client-side extension objects
 * @internal
 */
export const clientSideExtensionsKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('clientSideExtensionsKeyFacet', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for the SPHome context
 * @internal
 */
export const spHomeContextKeyFacet = new KeyFacet<{
  tenantRootUrl: string
}>('spHomeContextKeyFacet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for the user's frequent and favorites sites
 * @public
 */
export const spAllSitesKeyFacet = new KeyFacet<{
  tenantRootUrl: string
}>('spAllSitesKeyFacet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for an itemSet of followed sites
 * @internal
 */
export const spFollowedSitesItemSetKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('spFollowedSitesItemSet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for an itemSet of frequent sites
 * @internal
 */
export const spFrequentSitesItemSetKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('spFrequestSitesItemSet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for the itemSet of libraries in a Site
 * @public
 */
export const spLibrariesItemSetKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
}> = new KeyFacet('spLibrariesItemSet', {
  webAbsoluteUrl: stringPart()
});

/**
 * @public
 */
export const spOneDriveKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('oneDrive', {
  tenantRootUrl: stringPart()
});

/**
 * @public
 */
export const spOneDriveDocumentLibraryKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('oneDriveDocumentLibrary', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for whether tenant is an admin
 * @beta
 */
export const spTenantKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('spTenantKeyFacet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet to check if an item is followed
 */
export const followedItemKeyFacet: KeyFacet<{
  endpoint: string;
}> = new KeyFacet('followedItemKeyFacet', {
  endpoint: stringPart()
});

/**
 * Key facet for sp PeoplePicker DataSource instance
 * @internal
 */
export const spPeoplePickerDataSourceKeyFacet: KeyFacet<{
  webAbsoluteUrl: string;
  listFullUrl: string;
}> = new KeyFacet('spPeoplePickerDataSource', {
  webAbsoluteUrl: stringPart(),
  listFullUrl: stringPart()
});

/**
 * Key facet for the root item of an ODB Left Nav
 * @public
 */
export const businessNavSetKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('businessNavSet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for the pivots of an ODB Left Nav
 * @internal
 */
export const oneDriveNavSetKeyFacet: KeyFacet<{
  tenantRootUrl: string;
}> = new KeyFacet('oneDriveNavSet', {
  tenantRootUrl: stringPart()
});

/**
 * Key facet for the Shared Pivot
 * @public
 */
export const spOneDriveSharedKeyFacet: KeyFacet<{}> = new KeyFacet('spOneDriveShared', {});

/**
 * Key facet for the Discover Pivot
 * @public
 */
export const spOneDriveDiscoverKeyFacet: KeyFacet<{}> = new KeyFacet('spOneDriveDiscover', {});

/**
 * Key facet for the Recycle Bin Pivot
 * @public
 */
export const spOneDriveRecyleBinKeyFacet: KeyFacet<{}> = new KeyFacet('spOneDriveRecyleBin', {});